import {
  type BackupScope,
  type Condition,
  type UpdateControlViolationInput,
  type Configuration,
  type Environment,
  type DataClass,
  type ApplicationOverride,
  GetBackupScopeApi,
  type InventoryResource,
  type SnapshotStatus,
  type UpdateCloudResourceConfigurationRequest,
  type ListInventoryResourcesResponse,
  SaveBackupScopeApi,
} from '@repo/api-gw-sdk';
import {
  SnapshotsApi,
  ResourcesApi,
  ListInventoryApi,
  ListInventoryItemSnapshotsApi,
  ListInventoryAppsApi,
  ListInventorySourceRegionsApi,
  ListInventorySubnetsApi,
  ListInventoryNetworksApi,
  ListInventoryBackupRegionsApi,
  ListInventoryResourceTypesApi,
  ListInventoryEnvironmentsApi,
  ListControlViolationsApi,
  UpdateControlViolationApi,
  ListDataClassificationEntitiesApi,
  OverrideApplicationsApi,
  DeleteApplicationOverrideApi,
  CreateScanJobApi,
  ListInventoryResourceGroupsApi,
  ListInventoryTagsApi,
  GetResourceConfigurationApi,
  UpdateResourceConfigurationApi,
  ListInventoryResourceIdentifiersApi,
  UpdateShouldFullBackupResourceApi,
} from '@repo/api-gw-sdk';
import type { ColumnSort, PaginationState } from '@tanstack/react-table';

import { toJsonText } from '@/components/queryBuilder/QueryLanguageConverter';

import type { HttpClient } from './httpClient';

import { ApplicationsLogosWithAliases } from '../inventory/data';

const baseUrl = '/inventory';

export const inventory = (
  httpClient: HttpClient,
  config: Configuration,
  currentProjectId: string
) => {
  const listInventoryApi = new ListInventoryApi(config);
  const resourcesApi = new ResourcesApi(config);
  const listInventoryItemSnapshotsApi = new ListInventoryItemSnapshotsApi(
    config
  );
  const listInventoryAppsApi = new ListInventoryAppsApi(config);
  const listInventoryTagsApi = new ListInventoryTagsApi(config);
  const listInventorySubnetsApi = new ListInventorySubnetsApi(config);
  const listInventoryNetworksApi = new ListInventoryNetworksApi(config);
  const listInventoryEnvironmentsApi = new ListInventoryEnvironmentsApi(config);
  const listInventoryBackupRegionsApi = new ListInventoryBackupRegionsApi(
    config
  );
  const listDataClassificationEntitiesApi =
    new ListDataClassificationEntitiesApi(config);
  const listInventoryResourceTypesApi = new ListInventoryResourceTypesApi(
    config
  );
  const listInventorySourceRegionsApi = new ListInventorySourceRegionsApi(
    config
  );
  const listControlViolationsApi = new ListControlViolationsApi(config);
  const updateControlViolationApi = new UpdateControlViolationApi(config);

  const overrideApplicationsApi = new OverrideApplicationsApi(config);
  const deleteApplicationOverrideApi = new DeleteApplicationOverrideApi(config);

  const createScanJobApi = new CreateScanJobApi(config);
  const getSnapshotApi = new SnapshotsApi(config);
  const getResourceConfigurationApi = new GetResourceConfigurationApi(config);
  const updateResourceConfigurationApi = new UpdateResourceConfigurationApi(
    config
  );
  const listInventoryResourceGroupsApi = new ListInventoryResourceGroupsApi(
    config
  );
  const listInventoryResourceIdentifiersApi =
    new ListInventoryResourceIdentifiersApi(config);
  const updateShouldFullBackupResourceApi =
    new UpdateShouldFullBackupResourceApi(config);
  const getBackupScopeApi = new GetBackupScopeApi(config);
  const saveBackupScopeApi = new SaveBackupScopeApi(config);

  return {
    listAsync: (
      pagination: PaginationState,
      sorting: ColumnSort,
      columnFilters?: Condition
    ) =>
      listInventoryApi
        .listInventory(
          pagination.pageIndex,
          pagination.pageSize,
          `${sorting.id}:${sorting.desc ? 'desc' : 'asc'}`,
          currentProjectId,
          columnFilters ? toJsonText(columnFilters) : undefined
        )
        .then(normalizeInventoryList),
    list: (
      pagination: PaginationState,
      sorting: ColumnSort,
      columnFilters: Condition | undefined
    ) =>
      httpClient.execute(
        [baseUrl, pagination, sorting, columnFilters, currentProjectId],
        () =>
          listInventoryApi
            .listInventory(
              pagination.pageIndex,
              pagination.pageSize,
              `${sorting.id}:${sorting.desc ? 'desc' : 'asc'}`,
              currentProjectId,
              columnFilters ? toJsonText(columnFilters) : undefined
            )
            .then(normalizeInventoryList)
      ),
    listResourceIdentifiers: () =>
      httpClient.execute(
        [baseUrl, 'resource-identifiers', currentProjectId],
        () =>
          listInventoryResourceIdentifiersApi.listInventoryResourceIdentifiers(
            currentProjectId
          )
      ),
    get: (id: string) =>
      httpClient.execute([baseUrl, id, currentProjectId], () =>
        resourcesApi.getResource(id, currentProjectId).catch((x) => {
          if (x.code !== 404) {
            throw x;
          }
        })
      ),
    getMultiple: (ids: string[]) =>
      httpClient.execute([baseUrl, ids, currentProjectId], () =>
        Promise.all(
          ids.map((id) => resourcesApi.getResource(id, currentProjectId))
        )
      ),
    snapshots: {
      list: (
        resourceId: string,
        pageIndex: number,
        pageSize: number,
        statuses?: SnapshotStatus[],
        start?: Date,
        end?: Date
      ) => {
        return httpClient.execute(
          [
            baseUrl,
            'snapshots',
            resourceId,
            pageIndex,
            pageSize,
            currentProjectId,
            start?.toISOString(),
            end?.toISOString(),
            statuses?.map((s) => s.toString()).join(','),
          ],
          () =>
            listInventoryItemSnapshotsApi.listInventoryItemSnapshots(
              resourceId,
              pageIndex,
              pageSize,
              'eon',
              currentProjectId,
              start,
              end
            )
        );
      },
      get: (snapshotId?: string) =>
        httpClient.execute(
          [baseUrl, 'snapshot', snapshotId, currentProjectId],
          () =>
            snapshotId
              ? getSnapshotApi.getSnapshot(snapshotId, currentProjectId)
              : Promise.resolve(undefined)
        ),
    },
    tags: {
      list: () =>
        httpClient.execute([baseUrl, '/tags', currentProjectId], () =>
          listInventoryTagsApi.listInventoryTags(currentProjectId)
        ),
    },
    apps: {
      list: () =>
        httpClient.execute([baseUrl, '/apps', currentProjectId], () =>
          listInventoryAppsApi.listInventoryApps(currentProjectId)
        ),
      override: (resourceIds: string[], apps: string[]) => {
        return overrideApplicationsApi.overrideApplications(currentProjectId, {
          resourceIds,
          applications: apps,
        });
      },
      async deleteOverride(resources: InventoryResource[]) {
        const payload = resources.flatMap((res) => {
          return (res.classifications?.appsDetails?.apps || []).map((app) => {
            return {
              resourceId: res.id,
              name: app.name,
            } satisfies ApplicationOverride;
          });
        }) satisfies ApplicationOverride[];

        return deleteApplicationOverrideApi.deleteApplicationOverride(
          currentProjectId,
          payload
        );
      },
    },
    regions: {
      list: () =>
        httpClient.execute([baseUrl, '/source-regions', currentProjectId], () =>
          listInventorySourceRegionsApi.listInventorySourceRegions(
            currentProjectId
          )
        ),
    },
    subnets: {
      list: () =>
        httpClient.execute([baseUrl, '/subnets', currentProjectId], () =>
          listInventorySubnetsApi.listInventorySubnets(currentProjectId)
        ),
    },
    resourceGroups: {
      list: () =>
        httpClient.execute(
          [baseUrl, '/resource-groups', currentProjectId],
          () =>
            listInventoryResourceGroupsApi.listInventoryResourceGroups(
              currentProjectId
            )
        ),
    },
    networks: {
      list: () =>
        httpClient.execute([baseUrl, '/networks', currentProjectId], () =>
          listInventoryNetworksApi.listInventoryNetworks(currentProjectId)
        ),
    },
    resourceTypes: {
      list: () =>
        httpClient.execute([baseUrl, '/resource-types', currentProjectId], () =>
          listInventoryResourceTypesApi.listInventoryResourceTypes(
            currentProjectId
          )
        ),
    },
    environments: {
      list: () =>
        httpClient.execute([baseUrl, '/environments', currentProjectId], () =>
          listInventoryEnvironmentsApi.listInventoryEnvironments(
            currentProjectId
          )
        ),
      override: (resourceId: string, environment: Environment) => {
        return resourcesApi.overrideEnvironment(currentProjectId, resourceId, {
          environment,
        });
      },
      deleteOverride(resourceId: string) {
        return resourcesApi.removeEnvironmentOverride(
          currentProjectId,
          resourceId
        );
      },
    },
    dataClasses: {
      list: (resourceId: string) =>
        httpClient.execute(
          [baseUrl, 'classifications', resourceId, currentProjectId],
          () =>
            listDataClassificationEntitiesApi.listDataClassificationEntities(
              currentProjectId,
              resourceId
            )
        ),
      override: (resourceId: string, payload: DataClass[]) => {
        return resourcesApi.overrideDataClasses(currentProjectId, resourceId, {
          dataClasses: payload,
        });
      },
      deleteOverride: (resourceId: string) => {
        return resourcesApi.removeDataClassesOverride(
          currentProjectId,
          resourceId
        );
      },
    },
    backupRegions: {
      list: () =>
        httpClient.execute([baseUrl, '/backup-regions', currentProjectId], () =>
          listInventoryBackupRegionsApi.listInventoryBackupRegions(
            currentProjectId
          )
        ),
    },
    backupScope: {
      get: () =>
        httpClient.execute([baseUrl, '/backup-scope', currentProjectId], () =>
          getBackupScopeApi.getBackupScope(currentProjectId)
        ),
      save: (backupScope: BackupScope) =>
        saveBackupScopeApi.saveBackupScope(currentProjectId, backupScope),
    },
    violations: {
      list: (id: string) =>
        httpClient.execute([baseUrl, id, '/violations', currentProjectId], () =>
          listControlViolationsApi.listControlViolations(id, currentProjectId)
        ),
      update: (id: string, payload: UpdateControlViolationInput) =>
        updateControlViolationApi.updateControlViolation(
          id,
          currentProjectId,
          payload
        ),
    },
    configuration: {
      get: (id: string) =>
        httpClient.execute(
          [baseUrl, id, '/configuration', currentProjectId],
          () =>
            id
              ? getResourceConfigurationApi.getResourceConfiguration(
                  id,
                  currentProjectId
                )
              : Promise.resolve(undefined)
        ),
      update: (id: string, payload: UpdateCloudResourceConfigurationRequest) =>
        updateResourceConfigurationApi.updateResourceConfiguration(
          id,
          currentProjectId,
          payload
        ),
    },
    resources: {
      excludeResourceFromBackup: (resourceId: string) =>
        resourcesApi.excludeResourceFromBackup(currentProjectId, resourceId),
      cancelResourceBackupExclusion: (resourceId: string) =>
        resourcesApi.cancelResourceBackupExclusion(
          currentProjectId,
          resourceId
        ),
    },
    jobs: {
      createScanJob: (resourceId: string) => {
        return createScanJobApi.createScanJob(currentProjectId, { resourceId });
      },
      forceFullBackupForResource: (resourceId: string) => {
        return updateShouldFullBackupResourceApi.updateShouldFullBackupResource(
          resourceId,
          currentProjectId,
          {
            shouldRunFullBackup: true,
          }
        );
      },
    },
  };
};

function normalizeInventoryList(
  response: ListInventoryResourcesResponse
): ListInventoryResourcesResponse {
  response.resources.forEach((resource) => {
    if (!resource.classifications) {
      return;
    }
    resource.classifications.appsDetails = {
      apps: resource.classifications?.appsDetails?.apps?.filter((app, i) => {
        // Check if we're the only ones
        const icon = ApplicationsLogosWithAliases[app.name];
        const otherIcons =
          resource.classifications?.appsDetails?.apps?.findIndex(
            (otherApp, j) =>
              i < j && ApplicationsLogosWithAliases[otherApp.name] === icon
          );
        if (otherIcons === -1) {
          return true;
        }
        return false;
      }),
    };
  });

  return response;
}
